import type { CorrectComponentType, Format } from "../utils/index";
import { type Path } from "./makePathContextHooks";
import type { BaseOptions } from "./internal/types";

import html from "solid-js/html";
import { Show, For, createMemo, type Accessor } from "solid-js";
import { getTemplateContent } from "../utils/index.js";

export const makePathDataArrayComponent = <RootData = any, Props = any>({
  useRootData,
  hooks,
  name,
  arrayItemName,
}: BaseOptions<RootData> & { arrayItemName: string }): CorrectComponentType<
  {
    arrayPath: Path<RootData>;
    format?: Format;
  } & Props
> => {
  const { usePath, provide, usePathValue } = hooks;
  return (props, { element }) => {
    const itemTemplate = getTemplateContent(element, "item");
    if (!itemTemplate)
      return console.warn(
        `${name}: No template found with a property of 'item'.`,
        element,
      );
    if (!props.arrayPath)
      return console.warn(`${name}: No array path provided.`, element);

    const data = useRootData(element);

    const fullPath = usePath({
      element,
      path: () => props.arrayPath,
    });

    provide({
      element,
      path: fullPath,
    });

    const value = usePathValue({
      path: fullPath,
      data: data as any,
    });

    const arrayValue = createMemo(() => {
      const v = value();

      if (!v) {
        console.warn(
          `${name}: value is null or undefined at path ${fullPath()}`,
          element,
        );
        return null;
      }

      if (!Array.isArray(v)) {
        console.warn(
          `${name}: value is not an array at path ${fullPath()}`,
          element,
        );
        return null;
      }

      return v;
    });
    element.replaceChildren(...[]);

    return html`
      <${Show} when=${() => arrayValue() && arrayValue()!.length > 0}>
        <${For} each=${arrayValue}>
          ${(_: any, idx: Accessor<number>) => html`
            <${arrayItemName} item-index=${() => `${idx()}`}>
              ${() => Array.from(itemTemplate.cloneNode(true).childNodes)}
            </${arrayItemName}>
          `}
        <//>
      <//>
    `;
  };
};
